import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { errMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Lock,
  UserRound,
  ArrowLeft,
  Loader2,
  Eye,
  EyeOff,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params.get("expired")) toast("Session expired — please log in again.");
    if (user) {
      const next = params.get("next");
      navigate(next || (user.role === "admin" ? "/admin" : "/agent"), { replace: true });
    }
    // eslint-disable-next-line
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Enter username and password");
      return;
    }
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      const next = params.get("next");
      navigate(next || (u.role === "admin" ? "/admin" : "/agent"), { replace: true });
    } catch (e) {
      toast.error(errMessage(e, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen relative grid lg:grid-cols-2">
      {/* Left brand panel */}
      <aside className="hidden lg:flex relative overflow-hidden bg-gradient-primary text-primary-foreground p-12 flex-col justify-between">
        <div className="absolute inset-0 noise-overlay" />
        <svg viewBox="0 0 600 600" className="absolute -bottom-20 -right-20 w-[640px] opacity-30" aria-hidden="true">
          <defs>
            <linearGradient id="lg" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#fff" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
          </defs>
          <circle cx="300" cy="300" r="260" fill="url(#lg)" />
          <circle cx="300" cy="300" r="180" fill="url(#lg)" opacity="0.6" />
        </svg>

        <Link to="/" className="relative inline-flex items-center gap-3 text-sm font-medium hover:opacity-90">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <div className="relative">
          <span className="inline-flex items-center rounded-2xl bg-white shadow-elegant px-3.5 py-2.5">
            <img src={process.env.PUBLIC_URL + "/logo.png"} alt="1Rupee Clinic" className="h-11 w-auto object-contain" />
          </span>
          <h2 className="mt-6 font-display text-4xl xl:text-5xl font-bold tracking-tight leading-[1.1]">
            Sell health.
            <br />
            Earn every sale.
          </h2>
          <p className="mt-4 text-primary-foreground/85 max-w-md leading-relaxed">
            1Rupee Clinic agents help families enrol into our ₹100 membership and earn a
            commission on every card sold — paid out monthly.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            {[
              { k: "18+", v: "Clinics" },
              { k: "12k+", v: "Members" },
              { k: "24h", v: "Payouts" },
            ].map((s) => (
              <div
                key={s.k}
                className="rounded-xl bg-primary-foreground/10 ring-1 ring-primary-foreground/20 backdrop-blur p-3"
              >
                <div className="font-display font-bold text-2xl leading-none">{s.k}</div>
                <div className="text-[11px] uppercase tracking-[0.18em] mt-1.5 text-primary-foreground/80">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex items-center gap-2 text-xs text-primary-foreground/80">
          <ShieldCheck className="h-4 w-4" /> Encrypted credentials · JWT secured
        </div>
      </aside>

      {/* Right form */}
      <section className="relative bg-gradient-soft flex items-center justify-center p-6 sm:p-10">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <Card className="relative w-full max-w-md border-border/70 shadow-elegant">
          <CardContent className="p-7 sm:p-9">
            <Link to="/" className="lg:hidden inline-flex items-center gap-2 text-xs text-muted-foreground mb-4">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to home
            </Link>
            <Badge className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/10 mb-3">
              <Sparkles className="h-3.5 w-3.5 mr-1" /> Agent & Admin portal
            </Badge>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              Sign in to continue
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Use the credentials provided by your administrator.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="u">Username</Label>
                <div className="relative">
                  <UserRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="u"
                    placeholder="admin"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="h-11 pl-9"
                    autoComplete="username"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="p">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="p"
                    type={show ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pl-9 pr-10"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Toggle password visibility"
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-full h-11 bg-gradient-primary text-primary-foreground shadow-elegant rounded-xl"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                {loading ? "Signing in…" : "Sign in securely"}
              </Button>
            </form>

          </CardContent>
        </Card>
      </section>
    </main>
  );
}
